import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import { PublicLayout } from '@/app/layouts/PublicLayout'
import { SidebarLayout } from '@/app/layouts/SidebarLayout'
import {
  RequireDeclarationSigned,
  RequireFaceVerified,
  RequireNotBlocked,
  RequireSession,
  RequireStep,
  resumeFlowPath,
  SessionExpiryWatcher,
  useSessionStore,
} from '@/features/application-session'
import { FullScreenLoader } from '@/shared/components/FullScreenLoader'
import { paths, routePatterns } from './paths'

/**
 * Route-level code splitting.
 *
 * This is a public service used predominantly on South African mobile data, so a
 * citizen who bounces at the ID-number screen must not have downloaded the
 * camera pipeline or the signature canvas. Each route is its own chunk, and CI
 * enforces a first-load budget (scripts/check-bundle-size.mjs).
 */
const lazyPage = <K extends string>(
  loader: () => Promise<Record<K, React.ComponentType>>,
  key: K
) => lazy(() => loader().then((module) => ({ default: module[key] })))

const IdNumberPage = lazyPage(() => import('@/features/eligibility'), 'IdNumberPage')
const EligibilityCheckPage = lazyPage(
  () => import('@/features/eligibility'),
  'EligibilityCheckPage'
)
const FaceVerificationPage = lazyPage(
  () => import('@/features/face-verification'),
  'FaceVerificationPage'
)
const UnableToVerifyPage = lazyPage(
  () => import('@/features/face-verification'),
  'UnableToVerifyPage'
)
const ApplicationDetailsPage = lazyPage(
  () => import('@/features/application-details'),
  'ApplicationDetailsPage'
)
const ContactDetailsPage = lazyPage(
  () => import('@/features/contact-details'),
  'ContactDetailsPage'
)
const AddressDetailsPage = lazyPage(
  () => import('@/features/address-details'),
  'AddressDetailsPage'
)
const OfficerLoginPage = lazyPage(() => import('@/features/officer-login'), 'OfficerLoginPage')
const PhysicalVerificationPage = lazyPage(
  () => import('@/features/physical-verification'),
  'PhysicalVerificationPage'
)
const DeclarationPage = lazyPage(() => import('@/features/declaration'), 'DeclarationPage')
const ReviewPage = lazyPage(() => import('@/features/finalisation'), 'ReviewPage')
const SubmittedPage = lazyPage(() => import('@/features/finalisation'), 'SubmittedPage')
const TrackSearchPage = lazyPage(() => import('@/features/application-tracking'), 'TrackSearchPage')
const TrackStatusPage = lazyPage(() => import('@/features/application-tracking'), 'TrackStatusPage')
const HelpPage = lazyPage(() => import('@/features/static-pages'), 'HelpPage')
const PrivacyPage = lazyPage(() => import('@/features/static-pages'), 'PrivacyPage')
const TermsPage = lazyPage(() => import('@/features/static-pages'), 'TermsPage')
const AccessibilityPage = lazyPage(() => import('@/features/static-pages'), 'AccessibilityPage')

/**
 * `/apply` resumes a returning citizen at the earliest incomplete step.
 *
 * This is the junction every guard bounce passes through, so it must never be able
 * to send anyone back where they came from. It cannot: `firstIncompleteFlowStep`
 * answers `'idNumber'` for a session with no appRefNo, and `expireIfStale()`
 * guarantees an expired session HAS no appRefNo by the time this renders. Before
 * that guarantee existed, an expired-but-populated session resumed straight into the
 * guard that had just rejected it, forever.
 *
 * Selector-scoped rather than `useSessionStore()`, for the reason WizardGuards.tsx
 * sets out: a whole-store subscription re-renders this on every unrelated `set`, and
 * every render of a `<Navigate>` is another navigation.
 */
function ApplyIndexRedirect() {
  const state = useSessionStore(
    useShallow((s) => ({
      appRefNo: s.appRefNo,
      faceVerified: s.faceVerified,
      faceBlocked: s.faceBlocked,
      completedSteps: s.completedSteps,
      submittedAt: s.submittedAt,
    }))
  )
  // The bounce reason is still forwarded, but nothing renders off it any more: the
  // "your session expired" notice reads a one-shot flag off the store instead, because
  // several ways back to step 1 carry no route state at all. Kept because it costs
  // nothing and names, in a history entry, why the citizen was moved.
  const location = useLocation()
  return <Navigate to={resumeFlowPath(state)} replace state={location.state as unknown} />
}

export function AppRoutes() {
  return (
    <>
      {/*
        Renders nothing, and sits OUTSIDE the Suspense boundary deliberately.
        Every route below is lazy, so while a chunk is in flight React shows the
        fallback and commits nothing inside the boundary — a watcher mounted in there
        would not start applying the inactivity TTL until the very page it is meant to
        eject the citizen from had finished downloading.
      */}
      <SessionExpiryWatcher />

      <Suspense fallback={<FullScreenLoader message="Loading…" />}>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path={paths.home} element={<Navigate to={paths.apply.root} replace />} />
            <Route path={routePatterns.apply.root} element={<ApplyIndexRedirect />} />

            {/* Every step lives under the progress shell. */}
            <Route element={<SidebarLayout />}>
              {/* Step 1, and the only ungated screen: it is where a citizen with no
                session lands, and where the reference number comes from. */}
              <Route path={routePatterns.apply.idNumber} element={<IdNumberPage />} />

              <Route path={routePatterns.apply.wizard} element={<RequireSession />}>
                {/* Eligibility Check sits AFTER the reference number is minted, so
                  RequireSession has something to check. A static sibling of
                  /apply/id-number would have no reference at all and would bounce
                  straight back to it. */}
                <Route element={<RequireNotBlocked />}>
                  <Route
                    path={routePatterns.apply.eligibility}
                    element={<EligibilityCheckPage />}
                  />
                  <Route path={routePatterns.apply.verify} element={<FaceVerificationPage />} />
                </Route>
                <Route path={routePatterns.apply.unableToVerify} element={<UnableToVerifyPage />} />

                {/* Everything past this point requires a verified face. */}
                <Route element={<RequireFaceVerified />}>
                  <Route path={routePatterns.apply.details} element={<ApplicationDetailsPage />} />

                  <Route element={<RequireStep step="contact" />}>
                    <Route path={routePatterns.apply.contact} element={<ContactDetailsPage />} />
                  </Route>

                  <Route element={<RequireStep step="address" />}>
                    <Route path={routePatterns.apply.address} element={<AddressDetailsPage />} />
                  </Route>

                  <Route element={<RequireStep step="officer" />}>
                    <Route path={routePatterns.apply.officer} element={<OfficerLoginPage />} />
                  </Route>

                  <Route element={<RequireStep step="physicalVerification" />}>
                    <Route
                      path={routePatterns.apply.physicalVerification}
                      element={<PhysicalVerificationPage />}
                    />
                  </Route>

                  {/* Review comes BEFORE the declaration: the citizen checks what
                    is about to be sent, and only then signs it. */}
                  <Route element={<RequireStep step="review" />}>
                    <Route path={routePatterns.apply.review} element={<ReviewPage />} />
                  </Route>

                  <Route element={<RequireStep step="declaration" />}>
                    <Route path={routePatterns.apply.declaration} element={<DeclarationPage />} />
                  </Route>

                  {/* The receipt fires §4.10 finalise on mount, and §4.10 is not
                    idempotent — so this route is a submission, not a page view, and
                    needs a guard of its own. RequireStep cannot supply one:
                    `declaration` is the last wizard step, so it would only check the
                    steps before it. */}
                  <Route element={<RequireDeclarationSigned />}>
                    <Route path={routePatterns.apply.submitted} element={<SubmittedPage />} />
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route path={routePatterns.track.root} element={<TrackSearchPage />} />
            <Route path={routePatterns.track.status} element={<TrackStatusPage />} />

            <Route path={routePatterns.static.help} element={<HelpPage />} />
            <Route path={routePatterns.static.privacy} element={<PrivacyPage />} />
            <Route path={routePatterns.static.terms} element={<TermsPage />} />
            <Route path={routePatterns.static.accessibility} element={<AccessibilityPage />} />

            <Route path="*" element={<Navigate to={paths.home} replace />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  )
}
